import { useTranslation } from 'react-i18next';
import { firestoreDB } from './../../../services/Firebase/firebaseConfig'
import { collection, onSnapshot, query, where, updateDoc, doc, Timestamp } from 'firebase/firestore'
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import KpiCard from "../../Shared/KpiCard";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";
import { getUserFullName, truncate } from "../../../utils";
import moment from "moment";

const toDate = (value) => (value instanceof Timestamp ? value.toDate() : (value ? new Date(value) : null));
const countOf = (value) => (Array.isArray(value) ? value.length : (value && typeof value === 'object' ? Object.keys(value).length : 0));

/** Durée écoulée depuis le début, « 1 h 05 » ou « 12 min ». */
const formatDuration = (start, now) => {
    if (!start) return '–';
    const minutes = Math.max(0, Math.floor((now - start.getTime()) / 60000));
    if (minutes < 60) return `${minutes} min`;
    return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
};

/**
 * Lives en cours (/lives) : écoute Firestore en temps réel (myopinion/lives/channels, live_finished == false),
 * carte de statistiques, recherche et filtre d'état côté client, cartes avec durée, options du live et arrêt.
 */
const ListLive = () => {
    const { t } = useTranslation();

    const [lives, setLives] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [now, setNow] = useState(Date.now());
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('');
    const [stopping, setStopping] = useState(null);

    const handleEndLive = async (live) => {
        if (!window.confirm(t("Confirmer l'arrêt du live ?"))) return;
        setStopping(live.live_channel_name);
        try {
            await updateDoc(doc(firestoreDB, 'myopinion', 'lives', 'channels', live.live_channel_name), { live_finished: true });
        } catch (error) {
            window.alert(t("Impossible d'arrêter le live, veuillez réessayer."));
        } finally {
            setStopping(null);
        }
    };

    useEffect(() => {
        const q = query(collection(firestoreDB, 'myopinion', 'lives', 'channels'), where("live_finished", "==", false));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setLives(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
            setFetching(false);
        }, () => setFetching(false));
        const timer = setInterval(() => setNow(Date.now()), 30000); // durées à jour
        return () => { unsubscribe(); clearInterval(timer); };
    }, []);

    const enriched = lives
        .map(live => ({ ...live, _start: toDate(live.live_start_at), _audience: countOf(live.live_subscribers) }))
        .sort((a, b) => (b._start?.getTime() || 0) - (a._start?.getTime() || 0));

    const needle = search.trim().toLowerCase();
    const visible = enriched.filter(live => {
        if (needle && !`${getUserFullName(live.user, true)} ${live.user?.user_username || ''} ${live.live_description || ''}`.toLowerCase().includes(needle)) return false;
        if (filter === 'paused' && !live.live_on_pause) return false;
        if (filter === 'private' && !live.live_private_audience) return false;
        if (filter === 'special' && !live.live_special_content) return false;
        if (filter === 'adult' && !live.live_adult_live) return false;
        return true;
    });

    const durations = enriched.filter(l => l._start).map(l => (now - l._start.getTime()) / 60000);
    const longest = enriched.filter(l => l._start).sort((a, b) => a._start - b._start)[0];
    const stats = {
        live: enriched.length,
        paused: enriched.filter(l => l.live_on_pause).length,
        restricted: enriched.filter(l => l.live_private_audience || l.live_special_content || l.live_adult_live).length,
        audience: enriched.reduce((sum, l) => sum + l._audience, 0),
        avg: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0,
    };

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t("Lives en cours")} />

            {/* Statistiques (temps réel) */}
            <div className="row clearfix equal-height-row">
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-video-camera" tone="blue" label={t("En direct")} loading={fetching}
                             value={stats.live} hint={t("{{n}} en pause", { n: stats.paused })} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-clock-o" tone="green" label={t("Durée moyenne")} loading={fetching}
                             value={stats.live ? formatDuration(new Date(now - stats.avg * 60000), now) : '–'}
                             hint={longest ? t("Plus long : {{d}}", { d: formatDuration(longest._start, now) }) : t("Aucun live")} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-eye" tone="orange" label={t("Abonnés aux lives")} loading={fetching}
                             value={stats.audience} hint={t("Somme des abonnés des lives en cours")} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-lock" tone="purple" label={t("Accès restreint")} loading={fetching}
                             value={stats.restricted} hint={t("Privés, contenu spécial ou +18")} />
                </div>
            </div>

            {/* Recherche et filtre */}
            <div className="card">
                <div className="body">
                    <div className="row">
                        <div className="col-lg-8 col-md-12 mb-2">
                            <input className="form-control" placeholder={t("Rechercher : animateur ou description")} value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <div className="col-lg-4 col-md-12 mb-2">
                            <select className="form-control" value={filter} onChange={(e) => setFilter(e.target.value)}>
                                <option value="">{t("Tous les lives")}</option>
                                <option value="paused">{t("En pause")}</option>
                                <option value="private">{t("Audience privée")}</option>
                                <option value="special">{t("Contenu spécial")}</option>
                                <option value="adult">{t("+18")}</option>
                            </select>
                        </div>
                    </div>
                    <small className="text-muted"><span className="live-dot" /> {t("Mise à jour en temps réel · {{n}} live(s) affiché(s)", { n: visible.length })}</small>
                </div>
            </div>

            {fetching ? <FecthingSpinner /> : (
                <div className="row clearfix equal-height-row">
                    {visible.length === 0 ? (
                        <div className="col-md-12">
                            <div className="card"><div className="body text-center text-muted py-5">
                                {enriched.length === 0 ? t("Aucun live en cours") : t("Aucun live ne correspond aux filtres.")}
                            </div></div>
                        </div>
                    ) : visible.map(live => (
                        <div className="col-xl-4 col-lg-6 col-md-12" key={live.id}>
                            <div className="card live-card">
                                <div className="live-card__thumb" style={live.live_thumbnail ? { backgroundImage: `url(${live.live_thumbnail})` } : undefined}>
                                    <span className={`live-card__status ${live.live_on_pause ? 'is-paused' : ''}`}>
                                        {live.live_on_pause ? <><i className="fa fa-pause"></i> {t("En pause")}</> : <><span className="live-dot" /> {t("En direct")}</>}
                                    </span>
                                    <span className="live-card__duration"><i className="fa fa-clock-o"></i> {formatDuration(live._start, now)}</span>
                                </div>
                                <div className="live-card__body">
                                    <div className="live-card__host">
                                        <img src={live?.user?.profile?.prof_picture || MyOpinionLogo} alt="" />
                                        <div className="min-w-0">
                                            {live.user?.user_username ? (
                                                <Link to={`/users/${encodeURIComponent(live.user.user_username)}`} className="font-weight-bold text-truncate d-block">
                                                    {getUserFullName(live.user, true)}
                                                </Link>
                                            ) : <span className="font-weight-bold">{getUserFullName(live.user, true)}</span>}
                                            <small className="text-muted">
                                                {live._start ? t("Depuis le {{d}}", { d: moment(live._start).format('DD/MM/YYYY HH:mm') }) : ''}
                                            </small>
                                        </div>
                                    </div>
                                    <p className="live-card__description">{truncate(live.live_description, 140) || <em className="text-muted">{t("Aucune description")}</em>}</p>
                                    <div className="live-card__badges">
                                        {live.live_private_audience ? <span className="badge badge-dark ml-0"><i className="fa fa-lock"></i> {t("Privé")}</span> : null}
                                        {live.live_special_content ? <span className="badge badge-info ml-0">{t("Contenu spécial")}</span> : null}
                                        {live.live_adult_live ? <span className="badge badge-danger ml-0">+18</span> : null}
                                        {live.live_comments_blocked ? <span className="badge badge-secondary ml-0"><i className="fa fa-comment-o"></i> {t("Commentaires bloqués")}</span> : null}
                                        {live._audience ? <span className="badge badge-light border ml-0"><i className="fa fa-eye"></i> {t("{{n}} abonné(s)", { n: live._audience })}</span> : null}
                                    </div>
                                    <button type="button" className="btn btn-sm btn-outline-danger btn-block mt-auto" disabled={stopping === live.live_channel_name} onClick={() => handleEndLive(live)}>
                                        <i className="fa fa-stop-circle"></i>{' '}{stopping === live.live_channel_name ? t("Arrêt…") : t("Arrêter le live")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ListLive;
